import { TeamName } from "@/components/TeamName";
import { AppointmentStartEndButton } from "@/features/appointments/AppointmentActionButtons";
import { AppointmentStatusIndicator } from "@/features/appointments/AppointmentStatusIndicator";
import { CompactAppointmentQuestionnaire } from "@/features/appointments/CompactAppointmentQuestionnaire";
import { AppointmentTime } from "@/features/appointments/modals/components/AppointmentTime";
import {
  isCancelledStatus,
  isCompletedStatus,
  isOpenSlot,
} from "@/features/appointments/utils";
import {
  getAppointmentTitle,
  getIcon,
  getRenderTypeInfo,
} from "@/features/calendar/utils";
import { useModals } from "@/features/modals/hooks";
import { useActiveProject } from "@/features/project/ActiveProject";
import {
  type ExtendedAppointment,
  useAppointmentFromOpportunity,
} from "@repo/api";
import {
  Button,
  Icon,
  LightMode,
  Text,
  briefcaseIcon,
  personIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoWorkerName,
  Name,
  OneLineInfo,
  openNewProjectModal,
  useCustomer,
  useSettings,
} from "@repo/ui";
import { hasItems, isDefined, trackAnalytics } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useNavigate } from "react-router-dom";

import {
  ActionsCell,
  ActionsRow,
  DetailsCell,
  IconLabel,
  InfoCell,
  ItemIcon,
  LinkButton,
  TimeCell,
  TimeCellContent,
} from "./components";

export const AppointmentCells = ({
  item,
  mutateSearchAppointments,
  backgroundColor,
}: {
  item: ExtendedAppointment;
  mutateSearchAppointments?: () => void;
  backgroundColor?: string;
}) => {
  const navigate = useNavigate();
  const { pushModal } = useModals();
  const { task_configuration } = useSettings();
  const { opportunity } = useActiveProject();
  const { customer } = useCustomer(opportunity?.id);
  const shouldFetch = opportunity?.id ? false : true;
  const { data: appointmentDetails } = useAppointmentFromOpportunity(
    item,
    shouldFetch
  );
  const appointment = appointmentDetails || item;

  const textColor =
    isCancelledStatus(appointment.appointmentStatus) ||
    isCompletedStatus(appointment.appointmentStatus)
      ? LightMode.colourTextAndIcon3
      : LightMode.colourTextAndIcon1;

  return (
    <>
      <TimeCell backgroundColor={backgroundColor}>
        <TimeCellContent>
          <ItemIcon
            aria-hidden
            paths={getIcon(
              getRenderTypeInfo(appointment),
              undefined,
              task_configuration?.task_types
            )}
          />
          <AppointmentTime
            variant="AGENDA"
            status={appointment.appointmentStatus}
            startTime={appointment.timeWindowStartTime}
            endTime={appointment.timeWindowEndTime}
            customerTimezone={customer?.timezone}
          />
        </TimeCellContent>
      </TimeCell>
      <DetailsCell>
        <Text
          textStyle="Heading.XS"
          lineThrough={isCancelledStatus(appointment.appointmentStatus)}
          color={textColor}
          overflowHidden
        >
          {getAppointmentTitle(appointment)}
        </Text>
        <OneLineInfo
          small
          info={[
            appointment.locationName,
            appointment.assignedCoworker ? (
              <CoWorkerName email={appointment.assignedCoworker} fragment />
            ) : appointment.assignedTeam ? (
              <TeamName teamId={appointment.assignedTeam} fragment />
            ) : (
              t("label.unassigned")
            ),
            appointment.orderNumber !== undefined
              ? String(appointment.orderNumber)
              : undefined,
          ].filter(isDefined)}
        />
      </DetailsCell>
      <InfoCell>
        {!isOpenSlot(
          appointment.reasonCode,
          appointment.orderNumber,
          appointment.type
        ) &&
          appointment.type !== "DROP_IN" &&
          (appointment.customer.firstName || appointment.customer.lastName) && (
            <IconLabel>
              <Icon aria-hidden paths={personIcon} />
              <Text
                textStyle="Body.S"
                color={LightMode.colourTextAndIcon1}
                overflowHidden
              >
                <Name
                  firstName={appointment.customer.firstName}
                  lastName={appointment.customer.lastName}
                />
              </Text>
            </IconLabel>
          )}
        {appointment.opportunityId ? (
          <IconLabel>
            <Icon aria-hidden paths={briefcaseIcon} />
            <LinkButton>
              <Button
                type="plain"
                size="small"
                text={
                  isEmpty(appointment.opportunityTitle)
                    ? String(appointment.opportunityId)
                    : appointment.opportunityTitle
                }
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  navigate(`/opportunity/${appointment.opportunityId}`);
                  trackAnalytics("open_project_page");
                }}
              />
            </LinkButton>
          </IconLabel>
        ) : (
          !isOpenSlot(
            appointment.reasonCode,
            appointment.orderNumber,
            appointment.type
          ) && (
            <LinkButton>
              <Button
                type="plain"
                size="small"
                text={t("action.add_customer")}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openNewProjectModal({
                    pushModal,
                    resetModal: true,
                    associatedDropInAppointment: appointment,
                    onProjectCreated: (projectId: number) => {
                      navigate(`/opportunity/${projectId}/`);
                      trackAnalytics("new_project_created");
                    },
                    onSelectProject: (projectId: number) => {
                      navigate(`/opportunity/${projectId}/`);
                      trackAnalytics("manage_project");
                    },
                  });
                }}
              />
            </LinkButton>
          )
        )}
      </InfoCell>
      <InfoCell wide>
        <Text
          textStyle="Body.S"
          color={LightMode.colourTextAndIcon2}
          overflowHidden
        >
          {[
            !hasItems(appointment.planners?.designsInfo)
              ? t("prompt.no_drawings")
              : t("label.attached_drawings", {
                  number: appointment.planners?.designsInfo.length,
                }),
            !isEmpty(item.tasks)
              ? t("label.tasks_number", { tasks: item.tasks?.length })
              : t("prompt.no_tasks"),
          ].join(" | ")}
        </Text>
      </InfoCell>
      <InfoCell wide>
        <CompactAppointmentQuestionnaire
          opportunityId={appointment.opportunityId}
          additionalInformation={appointment.additionalInformation}
        />
      </InfoCell>
      <ActionsCell>
        <ActionsRow>
          <AppointmentStatusIndicator
            status={appointment.appointmentStatus}
            small
          />
          <AppointmentStartEndButton
            appointment={appointment}
            size="small"
            iconOnly
            mutateSearchAppointments={mutateSearchAppointments}
          />
        </ActionsRow>
      </ActionsCell>
    </>
  );
};
